import { apiInstance } from "api/config/axios";
import { IApiResponse } from "api/models/IApiResponse";
import { LessonFlat } from "models/LessonFlat";
import { getApiResponseData } from "api/services/api-service";
import { IUpdateLessonTagRequest } from "api/models/request/IUpdateLessonTagRequest";
import { IGetMyTrainingStatisticsResponse } from "api/models/response/IGetMyTrainingStatisticsResponse";
import { ICreateLessonRequest } from "api/models/request/ICreateLessonRequest";
import { UserTrainingStatistics } from "models/UserTrainingStatistics";
import { IGetUserTrainingInfoRequest } from "api/models/request/IGetUserTrainingInfoRequest";
import { IGetLessonDetailsRequest } from "api/models/request/IGetLessonDetailsRequest";
import { LessonDetails } from "models/LessonDetails";
import { IDeleteLessonRequest } from "api/models/request/IDeleteLessonRequest";
import { IEditLessonRequest } from "api/models/request/IEditLessonRequest";
import { IDeleteManyLessonsRequest } from "api/models/request/IDeleteManyLessonsRequest";
import { LiveTrainingData } from "models/LiveTrainingData";
import { LessonsTags } from "models/LessonsTags";
import { IUpdateLessonImageRequest } from "api/models/request/IUpdateLessonImageRequest";
import { IGetTrainingWithProgressRequest } from "api/models/request/IGetTrainingWithProgressRequest";
import { IGetTrainingWithProgressResponse } from "api/models/response/IGetTrainingWithProgressResponse";
import { IDeleteUserLessonsRequest } from "api/models/request/IDeleteUserLessonsRequest";
import { IGetLessonsByNodeFileRequest } from "api/models/request/IGetLessonsByNodeFileRequest";
import { IGetLessonImageRequest } from "@api/models/request/IGetLessonImageRequest";

const lessonApiBaseUrl = '/lesson';

export const getLessonDetails = (request: IGetLessonDetailsRequest & { workspaceId: string }): Promise<IApiResponse<LessonDetails>> => {
    return apiInstance.get(`${lessonApiBaseUrl}/${request.lessonId}/workspaces/${request.workspaceId}/lessonDetails`).then((response) => {
        return getApiResponseData(response);
    });
};

export const getLessonImage = (request: IGetLessonImageRequest & { workspaceId: string }): Promise<IApiResponse<Blob>> => {
    return apiInstance
        .get<Blob>(`${lessonApiBaseUrl}/workspaces/${request.workspaceId}/lessons/${request.lessonId}/image`, { responseType: 'blob' })
        .then((response) => ({
            success: true,
            data: response.data,
            status: response.status,
            message: response.statusText,
            errors: undefined,
        }))
        .catch((error) => ({
            success: false,
            data: undefined,
            status: error.response?.status || 500,
            message: error.response?.statusText || "An error occurred while fetching the profile photo",
            errors: error.response?.data?.errors || [{ code: "UNKNOWN_ERROR", message: "Unexpected error" }], // Ensure errors are structured correctly
        }));
}

export const getLessons = (request: { workspaceId: string }): Promise<IApiResponse<LessonFlat[]>> => {
    return apiInstance.get(`${lessonApiBaseUrl}/all/workspaces/${request.workspaceId}`).then((response) => {
        return getApiResponseData(response);
    });
};

export const getLessonsByNodeFile = (request: IGetLessonsByNodeFileRequest & {workspaceId: string}): Promise<IApiResponse<LessonFlat[]>> => {
    return apiInstance.get(`${lessonApiBaseUrl}/all/workspaces/${request.workspaceId}/node-files/${request.nodeFileId}`).then((response) => {
        return getApiResponseData(response);
    });
};

export const getMyTrainingStatistics = (request: IGetUserTrainingInfoRequest & { workspaceId: string }): Promise<IApiResponse<IGetMyTrainingStatisticsResponse>> => {
    const url = request.userId ? `${lessonApiBaseUrl}/training-info/workspaces/${request.workspaceId}/users/${request.userId}` : `${lessonApiBaseUrl}/my-training-info/workspaces/${request.workspaceId}`;
    return apiInstance.get(url).then((response) => {
        return getApiResponseData(response);
    });
};

export const getStatistics = (request: { workspaceId: string }): Promise<IApiResponse<UserTrainingStatistics[]>> => {
    return apiInstance.get(`${lessonApiBaseUrl}/statistics/workspaces/${request.workspaceId}`).then((response) => {
        return getApiResponseData(response);
    });
};

export const getUsersWithLiveTrainingInfo = (request: { userIds: string[]; lessonIds: string[]; } & { workspaceId: string }): Promise<IApiResponse<LiveTrainingData[]>> => {
    return apiInstance.post(`${lessonApiBaseUrl}/workspaces/${request.workspaceId}/live-training-progresses`, { userIds: request.userIds, lessonIds: request.lessonIds}).then((response) => {
        return getApiResponseData(response);
    });
}

export const addLesson = (request: ICreateLessonRequest & { workspaceId: string }): Promise<IApiResponse<any>> => {
    return apiInstance.post(`${lessonApiBaseUrl}/workspaces/${request.workspaceId}/create`, request).then((response) => {
        return getApiResponseData(response);
    });
}

export const editLesson = (request: IEditLessonRequest & { workspaceId: string }): Promise<IApiResponse<any>> => {
    return apiInstance.put(`${lessonApiBaseUrl}/${request.lessonId}/workspaces/${request.workspaceId}/edit`, request).then((response) => {
        return getApiResponseData(response);
    });
}

export const updateLessonImage = (request: IUpdateLessonImageRequest & { workspaceId: string }): Promise<IApiResponse<any>> => {
    return apiInstance.patch(`${lessonApiBaseUrl}/${request.lessonId}/workspaces/${request.workspaceId}/update-image`, request).then((response) => {
        return getApiResponseData(response);
    });
}

export const deleteLessonImage = (lessonId: string): Promise<IApiResponse<any>> => {
    return apiInstance.delete(`${lessonApiBaseUrl}/${lessonId}/delete-image`).then((response) => {
        return getApiResponseData(response);
    });
}

export const getLessonsTags = (request: {workspaceId: string}): Promise<IApiResponse<LessonsTags>> => {
    return apiInstance.get(`${lessonApiBaseUrl}/tags/workspaces/${request.workspaceId}`).then((response) => {
        return getApiResponseData(response);
    });
};

export const updateLessonTag = (request: IUpdateLessonTagRequest & { workspaceId: string }): Promise<IApiResponse<any>> => {
    return apiInstance.patch(`${lessonApiBaseUrl}/${request.lessonId}/workspaces/${request.workspaceId}/tag`, { newTag: request.newTag, tagType: request.tagType }).then((response) => {
        return getApiResponseData(response);
    });
}

export const deleteAssignedLessons = (request: IDeleteUserLessonsRequest & { workspaceId: string }): Promise<IApiResponse<any>> => {
    return apiInstance.delete(`${lessonApiBaseUrl}/workspaces/${request.workspaceId}/users/${request.userId}/assigned-lessons`).then((response) => {
        return getApiResponseData(response);
    });
}

export const deleteLesson = (request: IDeleteLessonRequest & { workspaceId: string } ): Promise<IApiResponse<any>> => {
    return apiInstance.delete(`${lessonApiBaseUrl}/${request.lessonId}/workspaces/${request.workspaceId}/delete`).then((response) => {
        return getApiResponseData(response);
    });
}

export const deleteManyLessons = (request: IDeleteManyLessonsRequest & { workspaceId: string } ): Promise<IApiResponse<any>> => {
    return apiInstance.delete(`${lessonApiBaseUrl}/workspaces/${request.workspaceId}/delete-many?lessonIds=${request.lessonIds.join(',')}`).then((response) => {
        return getApiResponseData(response);
    });
}

export const getTrainingWithProgress = (request: IGetTrainingWithProgressRequest & { workspaceId: string }): Promise<IApiResponse<IGetTrainingWithProgressResponse>> => {
    return apiInstance.get(`${lessonApiBaseUrl}/${request.lessonId}/workspaces/${request.workspaceId}/user-lesson-progress`).then((response) => {
        return getApiResponseData(response);
    });
};